import { AuthState } from "../base/AuthState";
import { IndexState } from "../index/IndexState";
import { ForgotPasswordState } from "./ForgotPasswordState";

import template from './LoginTemplate.html';
import { _lcs } from "../../i18n";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export class LoginState extends AuthState
{
    static ROUTE = '/login';
    static ID = 'login';

    async enter()
    {
        // Render into the layout host (<app-layout> renders an empty #main-wrapper
        // when logged out). Falls back to body if the layout isn't present.
        const host = this.app.container.querySelector( "#main-wrapper" ) || document.body;
        this.app.view.render( host, template );

        // Icons are static CSS (.icon--*) from the design system — no JS render.
        this.bindPasswordToggle();
        this.bindForm();

        const forgot = host.querySelector( '[data-id="link-forgot-password"]' );
        if ( forgot )
        {
            this.addEventListener( forgot, "click", ( e ) =>
            {
                e.preventDefault();
                this.app.router.redirect( ForgotPasswordState.ROUTE );
            } );
        }
    }

    bindPasswordToggle()
    {
        const toggle = document.getElementById( "togglePw" );
        const pw = document.getElementById( "password" );
        if ( !toggle || !pw )
        {
            return;
        }

        const icon = toggle.querySelector( ".icon" );

        // Tracked listener — InfrontJS removes it automatically on state exit.
        this.addEventListener( toggle, "click", () =>
        {
            const show = pw.type === "password";
            pw.type = show ? "text" : "password";
            toggle.setAttribute( "aria-pressed", String( show ) );
            toggle.setAttribute( "aria-label", show ? _lcs( "Passwort verbergen" ) : _lcs( "Passwort anzeigen" ) );
            if ( icon )
            {
                icon.classList.toggle( "icon--eye", !show );
                icon.classList.toggle( "icon--eye-off", show );
            }
            pw.focus();
        } );
    }

    bindForm()
    {
        const form = document.querySelector( ".auth__form" );
        if ( !form )
        {
            return;
        }

        const errorEl = form.querySelector( '[data-id="login-error"]' );
        const submitBtn = form.querySelector( 'button[type="submit"]' );

        this.addEventListener( form, "submit", async ( e ) =>
        {
            e.preventDefault();
            this._clearError( errorEl );

            const email = ( document.getElementById( "email" )?.value || "" ).trim();
            const password = document.getElementById( "password" )?.value || "";

            // Client-side validation.
            if ( !EMAIL_RE.test( email ) )
            {
                this._showError( errorEl, _lcs( "Bitte gib eine gültige E-Mail-Adresse ein." ) );
                return;
            }
            if ( !password )
            {
                this._showError( errorEl, _lcs( "Bitte gib dein Passwort ein." ) );
                return;
            }

            this._setLoading( submitBtn, true );
            try
            {
                const { result } = await this.app.api.post(
                    "/auth/session",
                    { email, password, app: "admin" }
                );

                if ( result && +result.status === 201 )
                {
                    // Persist the token, then let the App resolve the session
                    // (validate, load the user, enter the in-app shell).
                    this.app.storage.saveSessionToken( result.data.token );
                    await this.app.resolveSession();
                    this.app.router.redirect( IndexState.ROUTE );
                }
                else
                {
                    throw new Error( "Unexpected login response." );
                }
            }
            catch ( err )
            {
                this._setLoading( submitBtn, false );
                this._showError( errorEl, this._errorMessageForStatus( +err?.status ) );
            }
        } );
    }

    _errorMessageForStatus( status )
    {
        switch ( status )
        {
            case 400: return _lcs( "Bitte überprüfe deine Eingaben." );
            case 401:
            case 403: return _lcs( "Zugang verweigert. Prüfe deine Zugangsdaten oder Berechtigung." );
            case 404: return _lcs( "Kein Konto mit dieser E-Mail-Adresse gefunden." );
            default:  return _lcs( "Anmeldung fehlgeschlagen. Bitte versuche es später erneut." );
        }
    }

    _setLoading( btn, on )
    {
        if ( !btn )
        {
            return;
        }
        btn.disabled = on;
        btn.setAttribute( "aria-busy", String( on ) );
    }

    _showError( el, message )
    {
        if ( !el )
        {
            return;
        }
        el.textContent = message;
        el.hidden = false;
    }

    _clearError( el )
    {
        if ( !el )
        {
            return;
        }
        el.textContent = "";
        el.hidden = true;
    }

    async exit()
    {
    }
}
